/**
 * ParentGuardian CRUD client (multi-guardian feature, M6).
 *
 * Wraps the admin-only DRF endpoints at /oh_users/parent_guardian/. Used by
 * the Guardians panel inside the Alfred Parent profile view.
 */
import { sentryLogger } from 'sentry'

import { apiEndpoints } from './common'

import { get, patch, post, remove } from '../request'

const listGuardians = async (parentUuid) => {
  try {
    const response = await get(
      `${apiEndpoints.parentGuardian}/?parent=${parentUuid}`
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching parent guardians')
    throw error
  }
}

const createGuardian = async (payload) => {
  try {
    const response = await post(`${apiEndpoints.parentGuardian}/`, payload)
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in creating parent guardian')
    throw error
  }
}

const updateGuardian = async (uuid, payload) => {
  try {
    const response = await patch(
      `${apiEndpoints.parentGuardian}/${uuid}/`,
      payload
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in updating parent guardian')
    throw error
  }
}

const deleteGuardian = async (uuid) => {
  try {
    const response = await remove(`${apiEndpoints.parentGuardian}/${uuid}/`)
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in deleting parent guardian')
    throw error
  }
}

const setPrimaryGuardian = async (uuid) => {
  // Convenience wrapper — flips is_primary_outbound=true; the backend signal
  // demotes the previous primary and mirrors the new primary's phone into
  // User.phone_number.
  return updateGuardian(uuid, { is_primary_outbound: true })
}

// Duplicate-family merge: when the phone typed into "Add Guardian" already
// belongs to an existing parent, the backend offers to merge that family into
// this one. `previewParentMerge` reports whether the phone is an existing
// parent and what would move; `mergeParents` executes it.
const previewParentMerge = async ({ sourcePhone, targetParent }) => {
  try {
    const response = await get(
      `${apiEndpoints.parentMergePreview}?source_phone=${encodeURIComponent(
        sourcePhone
      )}&target_parent=${targetParent}`
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in previewing parent merge')
    throw error
  }
}

const mergeParents = async ({ sourceParent, targetParent, guardian }) => {
  try {
    const response = await post(apiEndpoints.parentMerge, {
      source_parent: sourceParent,
      target_parent: targetParent,
      guardian
    })
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in merging parents')
    throw error
  }
}

// Parent-initiated merge requests (member app) await admin approval. These
// admin-only helpers back the Merge Requests review queue: list the queue,
// then approve (runs the real merge) or reject with a reason.
const listMergeRequests = async (status = 'pending') => {
  try {
    const response = await get(
      `${apiEndpoints.parentMergeRequestAdmin}?status=${status}`
    )
    // DRF LimitOffsetPagination → { count, results }; tolerate a bare list too.
    return response.data?.results || response.data || []
  } catch (error) {
    sentryLogger(error, 'error in listing parent merge requests')
    throw error
  }
}

const approveMergeRequest = async (uuid) => {
  try {
    const response = await post(
      `${apiEndpoints.parentMergeRequestAdmin}${uuid}/approve/`,
      {}
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in approving parent merge request')
    throw error
  }
}

const rejectMergeRequest = async (uuid, reason) => {
  try {
    const response = await post(
      `${apiEndpoints.parentMergeRequestAdmin}${uuid}/reject/`,
      { reason }
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in rejecting parent merge request')
    throw error
  }
}

export {
  listGuardians,
  createGuardian,
  updateGuardian,
  deleteGuardian,
  setPrimaryGuardian,
  previewParentMerge,
  mergeParents,
  listMergeRequests,
  approveMergeRequest,
  rejectMergeRequest
}
